import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { TrendingUp, TrendingDown, Users, Mail, Zap, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * TrialAnalyticsPanel — admin view of trial conversion + A/B variants.
 *
 * Reads /api/admin/trial-analytics and renders:
 *  • 4 top-level KPIs (started / active / converted / fallback)
 *  • conversion + fallback + re-engagement rates
 *  • A/B variant table (trial length × analyses cap)
 */
export default function TrialAnalyticsPanel() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const bg = language === "bg";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/trial-analytics`);
      setData(d);
    } catch (e) {
      if (process.env.NODE_ENV === 'development') console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cardBg = isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-slate-100";
  const cardTitleCls = isDark ? "text-slate-400" : "text-slate-500";
  const cardValueCls = isDark ? "text-white" : "text-[#1A1A2E]";

  if (loading || !data) {
    return (
      <div className={`rounded-2xl border p-6 flex items-center gap-3 ${cardBg}`} data-testid="trial-analytics-loading">
        <Loader2 className="w-5 h-5 animate-spin text-sky-500" />
        <span className={cardTitleCls}>{bg ? "Зареждане на анализ..." : "Loading analytics..."}</span>
      </div>
    );
  }

  const totals = data.totals || {};
  const kpis = [
    { key: "started", label: bg ? "Пробни периоди" : "Trials started", value: totals.started ?? 0, icon: Users, color: "#0EA5E9" },
    { key: "active_trial", label: bg ? "Активни в момента" : "Active now", value: totals.active_trial ?? 0, icon: Zap, color: "#8B5CF6" },
    { key: "converted_paid", label: bg ? "Превърнати в платени" : "Converted → paid", value: totals.converted_paid ?? 0, icon: TrendingUp, color: "#10B981" },
    { key: "fallback_free", label: bg ? "Върнати към Free" : "Fallback → Free", value: totals.fallback_free ?? 0, icon: TrendingDown, color: "#F59E0B" },
  ];

  const rateBadge = (label, value, good) => (
    <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${cardBg}`} data-testid={`rate-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className={`text-[11px] font-medium ${cardTitleCls}`}>{label}</span>
      <span className={`text-sm font-bold ${good ? (isDark ? "text-emerald-300" : "text-emerald-600") : (isDark ? "text-amber-300" : "text-amber-600")}`}>
        {value}%
      </span>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="trial-analytics-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className={`text-lg font-bold ${cardValueCls}`}>{bg ? "Trial Conversion Dashboard" : "Trial Conversion Dashboard"}</h3>
          <p className={`text-xs mt-0.5 ${cardTitleCls}`}>
            {bg ? "Визия за успеха на пробните периоди + A/B variants" : "Visibility into trial success + A/B variants"}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} className="h-8 text-xs gap-1.5" data-testid="trial-analytics-refresh">
          <RefreshCw className="w-3.5 h-3.5" />
          {bg ? "Обнови" : "Refresh"}
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map(k => {
          const Icon = k.icon;
          return (
            <div key={k.key} className={`rounded-2xl border p-4 ${cardBg}`} data-testid={`trial-kpi-${k.key}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className={`text-[10px] font-semibold uppercase tracking-wider ${cardTitleCls}`}>{k.label}</span>
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${k.color}15` }}>
                  <Icon className="w-3.5 h-3.5" style={{ color: k.color }} />
                </div>
              </div>
              <p className={`text-2xl font-bold ${cardValueCls}`}>{k.value}</p>
            </div>
          );
        })}
      </div>

      {/* Rates */}
      <div className="flex flex-wrap gap-2">
        {rateBadge(bg ? "Conversion" : "Conversion", data.conversion_rate_pct ?? 0, (data.conversion_rate_pct ?? 0) >= 15)}
        {rateBadge(bg ? "Fallback" : "Fallback", data.fallback_rate_pct ?? 0, (data.fallback_rate_pct ?? 0) < 60)}
        <div className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${cardBg}`} data-testid="rate-reengagement">
          <Mail className={`w-3.5 h-3.5 ${isDark ? "text-slate-400" : "text-slate-500"}`} />
          <span className={`text-[11px] font-medium ${cardTitleCls}`}>
            {bg ? "Re-engagement" : "Re-engagement"}
          </span>
          <span className={`text-sm font-bold ${isDark ? "text-sky-300" : "text-sky-600"}`}>
            {totals.reengagement_sent ?? 0} {bg ? "изпратени" : "sent"} · {data.reengagement_conversion_pct ?? 0}%
          </span>
        </div>
      </div>

      {/* A/B Variants */}
      <div className={`rounded-2xl border overflow-hidden ${cardBg}`} data-testid="trial-variants-table">
        <div className="px-4 py-3 border-b border-slate-200/10">
          <h4 className={`text-sm font-semibold ${cardValueCls}`}>
            {bg ? "A/B Variants (дни × анализи)" : "A/B Variants (days × analyses)"}
          </h4>
          <p className={`text-[11px] mt-0.5 ${cardTitleCls}`}>
            {bg ? "3×3 матрица: 5/7/10 дни × 15/20/25 анализа — авто-hash bucketing по user_id" : "3×3 matrix: 5/7/10 days × 15/20/25 analyses — auto-hash bucketing by user_id"}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className={`text-[10px] uppercase tracking-wider ${isDark ? "text-slate-500 bg-slate-900/50" : "text-slate-400 bg-slate-50"}`}>
                <th className="px-4 py-2.5 font-semibold">Variant</th>
                <th className="px-3 py-2.5 font-semibold text-center">{bg ? "Старт" : "Started"}</th>
                <th className="px-3 py-2.5 font-semibold text-center">{bg ? "Платени" : "Paid"}</th>
                <th className="px-3 py-2.5 font-semibold text-center">Free</th>
                <th className="px-3 py-2.5 font-semibold text-center">{bg ? "Изтекли" : "Expired"}</th>
                <th className="px-3 py-2.5 font-semibold text-right">Conv. %</th>
              </tr>
            </thead>
            <tbody>
              {(data.variants || []).length === 0 ? (
                <tr>
                  <td colSpan={6} className={`px-4 py-6 text-center text-xs ${cardTitleCls}`}>
                    {bg ? "Все още няма данни за variants" : "No variant data yet"}
                  </td>
                </tr>
              ) : (
                (data.variants || []).map((v, idx) => {
                  const best = (v.conversion_rate || 0) === Math.max(...(data.variants || []).map(x => x.conversion_rate || 0));
                  const variantKey = `${v.trial_length_days}d-${v.trial_total_analyses}a`;
                  return (
                    <tr key={variantKey} className={`border-t ${isDark ? "border-slate-800" : "border-slate-100"}`} data-testid={`variant-row-${idx}`}>
                      <td className={`px-4 py-2.5 font-medium text-xs ${cardValueCls}`}>
                        {v.trial_length_days}d · {v.trial_total_analyses}a
                        {best && (v.started || 0) > 0 && (
                          <span className="ml-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500/15 text-emerald-500">BEST</span>
                        )}
                      </td>
                      <td className={`px-3 py-2.5 text-center text-xs ${cardValueCls}`}>{v.started || 0}</td>
                      <td className={`px-3 py-2.5 text-center text-xs text-emerald-500 font-semibold`}>{v.converted || 0}</td>
                      <td className={`px-3 py-2.5 text-center text-xs text-amber-500`}>{v.fallback || 0}</td>
                      <td className={`px-3 py-2.5 text-center text-xs ${cardTitleCls}`}>{v.expired || 0}</td>
                      <td className={`px-3 py-2.5 text-right text-xs font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{v.conversion_rate ?? 0}%</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className={`text-[10px] ${cardTitleCls}`} data-testid="trial-analytics-timestamp">
        {bg ? "Обновено" : "Updated"}: {new Date(data.ran_at).toLocaleString()}
      </p>
    </div>
  );
}
